import React, { useState, useMemo, useEffect } from 'react';

import moment, { Moment } from 'moment';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import ModalHeader from 'components/ModalHeader';
import MonthPicker from 'components/MonthPicker';
import { Mobile, Desktop } from 'components/Responsive';
import useStoreTranslation from 'hooks/useStoreTranslation';

import TripDate from './components/TripDate';
import Week from './components/Week';
import {
  Container,
  StickyHeader,
  Calendar,
  ConfirmButton,
  TripDates,
  ButtonArea,
  Month,
  NextIcon,
  PreviousIcon,
} from './styles';

export interface Props {
  show: boolean;
  currentInitialDate?: Date;
  currentFinalDate?: Date;
  onlyDate?: boolean;
  minRange?: number;
  allDate?: boolean;
  editingInitialDate?: boolean;
  type?: 'trip' | 'check-in-out' | 'default';
  onClose: () => void;
  onChange: (value: { initialDate: Date; finalDate?: Date }) => void;
  zIndex?: number;
}

const DateRangePicker = ({
  show,
  type,
  currentInitialDate,
  editingInitialDate,
  currentFinalDate,
  allDate,
  onlyDate,
  minRange,
  onClose,
  onChange,
  zIndex,
}: Props) => {
  const { t } = useStoreTranslation();
  const [calendar, setCalendar] = useState(moment());
  const [initialDate, setInitialDate] = useState<Date | undefined>();
  const [finalDate, setFinalDate] = useState<Date | undefined>();
  const [isEditingInitialDate, setIsEditingInitialDate] = useState(true);

  useEffect(() => {
    if (show) {
      setInitialDate(currentInitialDate);
      setIsEditingInitialDate(editingInitialDate !== undefined ? editingInitialDate : true);
      if (!onlyDate) setFinalDate(currentFinalDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const nextCalendar = useMemo(() => moment(calendar).add({ month: 1 }), [calendar]);

  const isValid = useMemo(() => Boolean(onlyDate ? initialDate : finalDate), [onlyDate, initialDate, finalDate]);

  const getMonthLabel = (calendar: Moment) => `${calendar.format('MMMM')} ${calendar.year()}`;

  const clearDates = () => {
    setInitialDate(undefined);
    setFinalDate(undefined);
  };

  const handleClose = () => {
    onClose();
    clearDates();
  };

  const handleChangeDate = (date: Date) => {
    if (isEditingInitialDate || onlyDate) {
      if (finalDate && moment(finalDate).isBefore(date)) {
        setFinalDate(undefined);
      }
      setIsEditingInitialDate(false);
      setInitialDate(date);
    } else {
      if (initialDate && moment(initialDate).isAfter(date)) {
        setInitialDate(undefined);
      }
      setIsEditingInitialDate(true);
      setFinalDate(date);
    }
  };

  const handleClickNextMonth = () => {
    setCalendar(moment(calendar).add({ month: 1 }));
  };

  const handleClickPreviousMonth = () => {
    setCalendar(moment(calendar).subtract({ month: 1 }));
  };

  const handleClickConfirm = () => {
    onChange({
      initialDate: initialDate as Date,
      finalDate,
    });
    handleClose();
  };

  return (
    <ApplicationOverlayContent show={show} onClose={onClose} zIndex={zIndex}>
      <Container>
        <Desktop>
          <ModalHeader title={t('dateRangePicker.title')} onClose={handleClose} />
        </Desktop>
        <Mobile>
          <AppBar noGoBack rightIcon="/images/close.svg" rightAction={handleClose}>
            {t('dateRangePicker.title')}
          </AppBar>
        </Mobile>

        <StickyHeader>
          <TripDates>
            <TripDates.Departure
              onClick={() => setIsEditingInitialDate(true)}
              className={isEditingInitialDate && !onlyDate ? 'active' : ''}
            >
              <TripDates.Label>
                {t(
                  (() => {
                    switch (type) {
                      case 'trip':
                        return 'dateRangePicker.departure';
                      case 'check-in-out':
                        return 'dateRangePicker.checkIn';
                      default:
                        return 'dateRangePicker.start';
                    }
                  })(),
                )}
              </TripDates.Label>
              <TripDate date={initialDate} />
            </TripDates.Departure>
            {!onlyDate && (
              <TripDates.Arrival
                onClick={() => setIsEditingInitialDate(false)}
                className={!isEditingInitialDate && !onlyDate ? 'active' : ''}
              >
                <TripDates.Label>
                  {t(
                    (() => {
                      switch (type) {
                        case 'trip':
                          return 'dateRangePicker.arrival';
                        case 'check-in-out':
                          return 'dateRangePicker.checkOut';
                        default:
                          return 'dateRangePicker.end';
                      }
                    })(),
                  )}
                </TripDates.Label>
                <TripDate date={finalDate} />
              </TripDates.Arrival>
            )}
          </TripDates>
          <Week />
        </StickyHeader>

        <Calendar>
          <Month>
            {getMonthLabel(calendar)}
            {allDate && (
              <>
                <PreviousIcon onClick={handleClickPreviousMonth} />
                <NextIcon onClick={handleClickNextMonth} />
              </>
            )}
          </Month>
          <MonthPicker
            allDate={allDate}
            onlyDate={onlyDate}
            year={calendar.year()}
            month={calendar.month()}
            initialDate={initialDate}
            finalDate={finalDate}
            minRange={minRange}
            onChange={handleChangeDate}
          />
          {allDate ? (
            <div>
              <Month>{getMonthLabel(nextCalendar)}</Month>
              <MonthPicker
                allDate={allDate}
                onlyDate={onlyDate}
                year={nextCalendar.year()}
                month={nextCalendar.month()}
                initialDate={initialDate}
                finalDate={finalDate}
                minRange={minRange}
                onChange={handleChangeDate}
              />
            </div>
          ) : (
            Number(11)
              .toArray()
              .map((month) => {
                const monthCalendar = moment(calendar).add({ month });
                return (
                  <div key={month}>
                    <Month>{getMonthLabel(monthCalendar)}</Month>
                    <MonthPicker
                      allDate={allDate}
                      onlyDate={onlyDate}
                      year={monthCalendar.year()}
                      month={monthCalendar.month()}
                      initialDate={initialDate}
                      finalDate={finalDate}
                      minRange={minRange}
                      onChange={handleChangeDate}
                    />
                  </div>
                );
              })
          )}
        </Calendar>
        <ButtonArea>
          <ConfirmButton type="button" disabled={!isValid} onClick={handleClickConfirm} testId="calendar-submit-button">
            {t('dateRangePicker.confirmButton')}
          </ConfirmButton>
        </ButtonArea>
      </Container>
    </ApplicationOverlayContent>
  );
};

DateRangePicker.defaultProps = {
  type: 'default',
};

export default DateRangePicker;
